import {
  clip,
  codes,
  ellipsis,
  hanging,
  highlight,
  MARGIN,
  mark,
  pad,
  spread,
  width as segWidth,
  wrap,
  type Seg,
  type Span,
} from './text.ts';
import { itemActions, type CatalogOption, type Needs } from '../engine/catalog.ts';
import type { Row } from '../engine/families.ts';
import { evalCondition, type OverlayCall, type OverlayCommand, type SessionEntity, type StreamEntry, type Term } from '../engine/state.ts';
import { days, daysBetween } from '../../shared/dates.ts';
import { speakerLabel, speakerOf } from '../../shared/speech.ts';
import { entityClass, plainText, type EntityKind, type EntityMention } from '../../shared/entities.ts';
import type { GameContent, Portrait, SaveState, TransitionDef } from '../../shared/types.ts';

/** Состояние → строки. Всё, что попадает на экран, сначала становится строками знаков. */

/**
 * Кто говорит (07-оболочка-тз, «Кто говорит»). Три слоя разведены цветом и
 * отступом, объяснять игроку ничего не надо; сам признак живёт в `shared/speech`,
 * потому что по нему же предпросмотр ищет реплику Марго.
 *
 * Названный собеседник (`> тоби — ...`) получает имя перед репликой цветом
 * ремарки: отдельного цвета на каждого говорящего не заводится — их может быть
 * сколько угодно, и различать их должно имя, а не оттенок, который надо помнить.
 */
/**
 * Колонка говорящего (07-оболочка-тз, «Метка говорящего и отдельная колонка»).
 *
 * Реплика — двухколоночная строка: имя в левой колонке, речь в правой. Это
 * транскрипт, а не проза с подписями: имя повторяется у каждой реплики, даже
 * когда человек говорит дважды подряд, и строка не зависит от соседней.
 *
 * Колонка **не является постоянным полем потока**: описание, текст комнаты,
 * цитата и системная строка занимают обе колонки и начинаются от обычного
 * левого края. Поэтому сцена без диалога не получает пустого отступа.
 */
export const SPEAKER_WIDTH = 10;

/**
 * Имя прижато к левому краю, а не к колонке речи (ТЗ разрешает оба, базовым
 * называя правый). Правый край давал рваную пустоту слева: короткое «ТОБИ»
 * висело в середине поля, и глаз не находил, где начинается строка. Левый край
 * выстраивает имена в столбик под текстом сцены — это и есть транскрипт.
 *
 * Имя длиннее колонки её не ломает: оно сдвигает свою первую строку и только
 * её. Обрезать имя нельзя — метка служебная, но читает её человек.
 */
const SPEAKER_GAP = 1;

/**
 * Уже колонки речи не остаётся: на телефоне двенадцать знаков под имя съедают
 * треть строки. Там та же структура показывается в строку — `МАРГО · текст`.
 */
const MIN_SPEECH = 34;

/**
 * Разделитель раундов — тонкая черта перед введённой командой.
 *
 * Раунд здесь — «что произошло после одного хода игрока». Пока реплики
 * короткие, границу держит само эхо команды; но стоит ответу занять пол-экрана,
 * и, вернувшись к потоку, человек не находит, откуда читать: серое эхо тонет
 * в стене текста.
 *
 * Черта намеренно **тоньше рамки**: рамка делит поверхность («выше мир, ниже
 * ты»), а это деление внутри одного разговора. Точечный глиф вместо сплошного
 * и ширина по тексту, а не по сетке, — чтобы линия читалась как пауза, а не
 * как второй интерфейсный шов.
 */
const ROUND = '┈';

export function streamLines(
  entries: StreamEntry[],
  max: number,
  focus: EntityMention | null = null,
  /** Сокращения экранных имён из `episode.yaml`: `полицейский → полиц.` */
  speakers: Record<string, string> = {},
): Seg[][] {
  const out: Seg[][] = [];
  // Ширина сетки: `max` приходит уже без полей, а колонка имени считается от края.
  const cols = max + MARGIN.text + MARGIN.right;
  const inline = max - SPEAKER_WIDTH - SPEAKER_GAP < MIN_SPEECH;
  const textCol = MARGIN.text + SPEAKER_WIDTH + SPEAKER_GAP;

  const rule = (): Seg[] => [{ text: pad() }, { text: ROUND.repeat(Math.max(1, max)), cls: 'round' }];

  entries.forEach((entry, index) => {
    if (out.length > 0) out.push([]);
    // Черту ставим перед командой: ниже неё — то, что случилось в ответ.
    // Перед самой первой не ставим: делить там нечего.
    if (entry.kind === 'echo' && index > 0) out.push(rule());
    /*
     * Граница нового посещения — та же тонкая черта и по той же причине: это
     * деление внутри транскрипта, а не монтаж и не смена места. Своего текста
     * у записи нет, подписи «возвращение» тоже: черта говорит всё сама.
     */
    if (entry.kind === 'visit') {
      out.push(rule());
      return;
    }
    /*
     * Подсвечивается только размеченное автором (07-оболочка-тз, «Явно
     * размеченные сущности»), и ровно те вхождения, которые он разметил:
     * счётчик общий на всю запись, поэтому перенос строки не сбивает счёт.
     */
    const spans = entitySpans(entry, focus);
    const seen = new Map<string, number>();
    /*
     * Листинг — машина показывает экран (07-оболочка-тз, «Разметка»): текст
     * внутри ``` не проза. Его не переносят по словам и не разбирают на
     * говорящих — у него значимы собственные пробелы, на них держится и
     * столбик таблицы, и отступ формулы. Сами ограждения не печатаются:
     * игрок видит окно редактора, а не разметку заметки.
     */
    let fence: string | null = null;

    for (const source of entry.text.split('\n')) {
      const edge = /^\s*(```+|~~~+)\s*(\S*)\s*$/.exec(source);
      if (edge && (fence == null || source.trim().startsWith(fence))) {
        fence = fence == null ? edge[1]! : null;
        continue;
      }
      if (fence != null) {
        // Табуляция в сетке знаков не живёт: одна клетка, а рисуется шире.
        const code = source.replace(/\t/g, '    ');
        // Строка длиннее колонки обрезается многоточием: у окна машины есть
        // край, а перенос по словам сломал бы столбик таблицы и отступ формулы.
        const text = code.length > max ? `${code.slice(0, max - 1)}…` : code;
        out.push([{ text: pad() }, { text, cls: 'listing' }]);
        continue;
      }
      // Эхо команды и карточки говорят не голосом персонажа — разметку к ним
      // не применяем: у них свой класс и свой цвет.
      const said = entry.kind === 'text' ? speakerOf(source) : { voice: entry.kind, name: null, text: source };
      const cls: string = said.voice;
      const label = said.name == null ? null : speakerLabel(said.name, speakers);

      // Обратные кавычки снимаются до переноса: иначе они займут колонки.
      const plain = codes(label != null && inline ? `${label} · ${said.text}` : said.text);
      const marks = entry.kind === 'text' ? [...plain.spans, ...spans] : [];

      // Реплика с колонкой: текст переносится по своей колонке, имя стоит
      // в первой строке и по правому краю — так оно примыкает к речи.
      if (label != null && !inline) {
        const width = Math.max(1, cols - textCol - MARGIN.right);
        // Имя длиннее колонки занимает свою строку целиком: втискивать его
        // в колонку значило бы съесть промежуток и склеить имя с речью,
        // а обрезать — соврать. Строкой больше, зато сетка ровная.
        const own = MARGIN.text + label.length >= textCol;
        if (own) out.push([{ text: `${pad()}${label}`, cls: 'remark' }]);

        wrap(plain.text, width).forEach((line, i) => {
          const named = i === 0 && !own;
          out.push([
            { text: named ? `${pad()}${label}`.padEnd(textCol) : ' '.repeat(textCol), cls: 'remark' },
            ...mark(line, cls, marks, seen),
          ]);
        });
        continue;
      }

      // Эхо печатается с висящим промптом, как это делает терминал: прокрутка
      // назад выглядит как одна колонка с торчащими приглашениями.
      wrap(plain.text, max).forEach((line, i) => {
        const lead = entry.kind === 'echo' && i === 0 ? hanging() : pad();
        // В строчном режиме имя живёт только в первой строке — перенос его
        // не повторяет, продолжение идёт обычным отступом.
        const head = inline && label != null && i === 0 && line.startsWith(label);
        const segs =
          head ?
            [
              { text: label, cls: 'remark' },
              { text: ' · ', cls: 'dim' },
              ...mark(line.slice(label.length + 3), cls, marks, seen),
            ]
          : mark(line, cls, marks, seen);
        out.push([{ text: lead, cls: 'dim' }, ...segs]);
      });
    }
  });
  return out;
}

/**
 * Упоминания записи → подсветка. Одинаковые формы сливаются в один span:
 * искать одно и то же по строке дважды незачем, а номера вхождений при этом
 * складываются.
 *
 * `focus` — сущность, открытая в контекстной панели: её последнее упоминание
 * получает дополнительную пометку поверх обычной подсветки, чтобы глаз нашёл
 * в потоке именно то место, о котором сейчас читают.
 */
function entitySpans(entry: StreamEntry, focus: EntityMention | null): Span[] {
  const byText = new Map<string, Span>();
  for (const mention of entry.mentions ?? []) {
    const focused =
      focus != null &&
      focus.kind === mention.kind &&
      focus.id === mention.id &&
      focus.label === mention.label &&
      focus.nth === mention.nth;
    const key = `${mention.label}\u0000${focused ? 'focus' : ''}`;
    const span = byText.get(key) ?? {
      text: mention.label,
      cls: focused ? `${entityClass(mention.kind)} focus` : entityClass(mention.kind),
      nth: new Set<number>(),
    };
    span.nth!.add(mention.nth);
    byText.set(key, span);
  }
  return [...byText.values()];
}

/**
 * Текст карточки перехода ([[14-переходы-и-даты-тз]], «Исполнение и экран»).
 *
 * Три строки и ни одной лишней: подпись монтажной связи, дата, место. Всё
 * берётся только из файла перехода — в этом и смысл замены автоматической
 * карточки авторской. Рамку, печать и ожидание `Enter` рисует тот же компонент,
 * что титр: второго визуального стиля у полноэкранного кадра нет.
 */
export function transitionCard(def: TransitionDef): string {
  return [def.timeLabel, def.date, def.location].filter((line) => line != null && line !== '').join('\n');
}

/**
 * Что показывает статус: дата текущей заметки, дальше сроки — `12.05.2026 · BLUE
 * CARD 233 дня`. Срок считается от той же даты, поэтому он сам тикает по ходу
 * пролога, и отдельного «сколько осталось» в контенте держать не надо.
 *
 * Прошедший срок показывается словом (`BLUE CARD истекла`), а не отрицательным
 * числом: минус в статусе читается как сбой машины, а не как факт о карте.
 *
 * Срок с непонятной датой не показывается вовсе: соврать в статусе хуже, чем
 * промолчать, а поймает это валидатор при загрузке.
 */
export function statusText(date: string | null, terms: Term[]): string {
  if (!date) return '';
  const parts = [date];
  for (const term of terms) {
    const left = daysBetween(date, term.at);
    if (left == null) continue;
    parts.push(`${term.label} ${left < 0 ? term.expired : days(left)}`);
  }
  return parts.join(' · ');
}

/**
 * Статус — тускло и по полям текста, а не по краю сетки: он часть текстового
 * блока, а не отдельная панель. Своей линейкой не отделяется — он должен
 * выглядеть как то, что может исчезнуть.
 *
 * Слева — где Марго находится, справа — дата и сроки (07-оболочка-тз, «Экран»).
 * Место подрезается, дата — никогда: дата и срок Blue Card это собственная
 * строка машины, а обрезанное число врёт. Место, которому осталась пара знаков,
 * не подрезается, а исчезает: `ко…` не говорит ничего.
 */
const PLACE_MIN = 6;

export function statusLine(place: string, text: string, cols: number): Seg[] {
  if (place === '' && text === '') return [];

  const right = text === '' ? 0 : MARGIN.right + text.length;
  const room = fit(place, cols - MARGIN.text - right - 1);
  const gap = Math.max(0, cols - right - MARGIN.text - room.length);

  // Место тускнеет вместе с датой: это одна машинная полоса, а не две.
  const segs: Seg[] = [{ text: ' '.repeat(MARGIN.text) }];
  if (room !== '') segs.push({ text: room, cls: 'echo' });
  segs.push({ text: ' '.repeat(gap) });
  if (text !== '') segs.push({ text, cls: 'echo' });
  return segs;
}

/** Подрезка с многоточием. Короче `PLACE_MIN` — пусто: обрывок не читается. */
function fit(text: string, room: number): string {
  if (text === '' || room < PLACE_MIN) return '';
  return text.length <= room ? text : `${text.slice(0, room - 1).trimEnd()}…`;
}

/**
 * Окно в поток: ровно `rows` строк.
 *
 * Пока текст в окно помещается, он пишется **сверху**, с первой свободной строки:
 * сцена читается как страница, которая заполняется, а не как лента, ползущая
 * вверх от пустого места. Переполнилось — окно едет за концом, свежий текст
 * оказывается рядом со строкой ввода, а старый уходит наверх.
 *
 * `scroll` считается от низа и здесь же зажимается — снаружи его можно крутить
 * сколько угодно, экран не сломается.
 */
export function viewport(lines: Seg[][], rows: number, scroll: number): Seg[][] {
  if (lines.length <= rows) {
    const tail = Array.from({ length: rows - lines.length }, () => [] as Seg[]);
    return [...lines, ...tail];
  }

  const clamped = Math.max(0, Math.min(scroll, lines.length - rows));
  const end = lines.length - clamped;
  return lines.slice(end - rows, end);
}

/**
 * Портрет в полублоках: `▀` красит верхнюю половину клетки цветом текста, нижнюю —
 * цветом фона. Одна клетка = два независимых пикселя, а поскольку клетка
 * моноширинного шрифта примерно 1:2, полупиксель выходит почти квадратным.
 */
export function portraitLines(portrait: Portrait): Seg[][] {
  const lines: Seg[][] = [];
  for (let y = 0; y + 1 < portrait.height; y += 2) {
    const top = portrait.grid[y] ?? '';
    const bottom = portrait.grid[y + 1] ?? '';
    const row: Seg[] = [];

    for (let x = 0; x < portrait.width; x++) {
      const upper = portrait.colors[top[x] ?? ' '];
      const lower = portrait.colors[bottom[x] ?? ' '];

      // Прозрачную половину нельзя рисовать `▀` без цвета: знак возьмёт цвет
      // текста и половина клетки окажется закрашенной. Поэтому знак выбирается
      // по тому, какие половины вообще есть.
      if (upper && lower) row.push({ text: '▀', color: upper, bg: lower });
      else if (upper) row.push({ text: '▀', color: upper });
      else if (lower) row.push({ text: '▄', color: lower });
      else row.push({ text: ' ' });
    }
    lines.push(row);
  }
  return lines;
}

/**
 * Помета `advance` в интерфейсе (07-оболочка-тз, «Опция»).
 *
 * Треугольник служебный: игрок его не вводит, поиск и дополнение его не учитывают,
 * в эхо команды он не попадает. Цвет не единственный сигнал — ту же работу
 * делает знак.
 */
export const ADVANCE_MARK = '▶';
export const ADVANCE_LEGEND = 'продолжает историю; к текущим действиям нельзя вернуться';

/**
 * Помета `earned` в интерфейсе (07-оболочка-тз, «Индикация заработанных реплик
 * и потери предметов»).
 *
 * Значок — единственный сигнал: цвет команды остаётся цветом её категории,
 * потому что заработанной бывает и реплика в разговоре, и действие с вещью.
 * Чем именно возможность открыта, оболочка не говорит — это знает сама сцена.
 *
 * И значок не означает «правильный ответ»: это ещё один путь, а не лучший.
 * Общий смысл объясняется один раз, на экране управления.
 */
export const EARNED_MARK = '✦';
export const EARNED_LEGEND = 'открыто тем, что Марго уже сделала или узнала; не обязательно лучший ход';

/**
 * Подпись требования: что команда использует и что именно.
 *
 * Названо своим словом, а не общим «нужно»: цвет говорит, какого рода вещь, но
 * цвет — не единственный канал, и строка деталей обязана сказать то же прямо.
 * Заодно снимается падеж: «использует предмет бланк» выговаривается в любом
 * роде, а «нужен бланк» / «нужна книга» оболочка не выговорит.
 */
export const USES: Record<Needs['kind'], string> = {
  word: 'использует слово',
  item: 'использует предмет',
};

/**
 * Шкала времени раунда «надавить» ([[07b-надавить-тз]], «Представление»).
 *
 * Остаток нельзя кодировать одним цветом: шкала убывает геометрически и рядом
 * с ней стоит текстовая метка — секунды словом. Счёта побед у неё нет и быть
 * не может: движок не знает, кто кого переиграл.
 */
export const TIMERS: Record<string, { full: string; empty: string; width: number }> = {
  blocks: { full: '▰', empty: '▱', width: 20 },
  dots: { full: '•', empty: '·', width: 24 },
};

export function timerGlyphs(name: string): { full: string; empty: string; width: number } {
  const found = TIMERS[name];
  if (found == null) throw new Error(`нет шкалы времени "${name}"; есть: ${Object.keys(TIMERS).join(', ')}`);
  return found;
}

/** Строка шкалы: убывающая полоса и секунды словом. */
export function timerSegs(left: number, total: number, glyphs: { full: string; empty: string; width: number }): Seg[] {
  const share = total <= 0 ? 0 : Math.max(0, Math.min(1, left / total));
  // Пока осталось хоть что-то, видна хотя бы одна клетка: пустая шкала значит
  // «время вышло», и врать об этом за полсекунды до конца нельзя.
  const filled = left > 0 ? Math.max(1, Math.round(share * glyphs.width)) : 0;
  const seconds = Math.ceil(left / 1000);

  return [
    { text: glyphs.full.repeat(filled), cls: 'timer' },
    { text: glyphs.empty.repeat(glyphs.width - filled), cls: 'timer-gone' },
    { text: `  ${seconds} с`, cls: 'timer' },
  ];
}

/**
 * Короткая форма предупреждения — когда в строке помет стоит ещё и требование.
 * Полная объясняет знак `▶` тому, кто видит его впервые, но обе пометы в одну
 * строку не влезают, а знак в списке к этому моменту уже сказал главное.
 */
export const ADVANCE_SHORT = `${ADVANCE_MARK} необратимо`;

/** Маркер выбора: одна зарезервированная позиция в начале каждой строки. */
export const PICK_MARK = '›';

/** Хоткеи: показаны прямо в полосе, отдельной строки под них нет. */
/*
 * Клавиша рядом с командой в служебной полосе (07-оболочка-тз, «Клавиши»).
 *
 * У `1`, `2` и `3` смысл с командой не совпадает: команда открывает полное
 * хранилище, а клавиша — контекстную панель по одной сущности. Подписаны они
 * всё равно рядом, потому что ищут их вместе и об одном и том же; разницу
 * игрок видит в первую же секунду после нажатия.
 */
const HOTKEY: Partial<Record<string, string>> = {
  справочник: '1',
  дело: '2',
  инвентарь: '3',
  меню: '0',
};

/** Экран управления командой не является и живёт на своей клавише. */
const MANUAL_HINT = '?';

/** Категория опции → класс. Цвет — из палитры рендерера, здесь только связь. */
function kindClass(option: CatalogOption): string {
  if (option.system) return 'system';
  return option.attrs.advance ? 'advance' : option.kind === 'environment' ? 'environment' : 'story';
}

/**
 * На чём команда держится (07-оболочка-тз, «Команда, которой нужна вещь или
 * слово»): вещь на руках — цветом предмета, слово «Дела» — цветом слова. Тем
 * же, которым они подсвечены в тексте: цвет значит одно и то же везде.
 */
export function needsClass(option: CatalogOption): string {
  return option.needs == null ? '' : option.needs.kind === 'word' ? 'needs-word' : 'needs-item';
}

/**
 * Строка свёрнутой семьи: `осмотреть… (6)` (07-оболочка-тз, «Сворачивание
 * повторяющихся действий комнаты»).
 *
 * Опцией игры она не является, поэтому и выглядит иначе — многоточием и числом,
 * — но цвет и категорию наследует у первой своей команды: семья стоит на её
 * месте, и список от сворачивания не перекрашивается.
 */
function familySegs(row: Extract<Row, { kind: 'family' }>, picked: boolean): Seg[] {
  const marks = [kindClass(row.sample), picked ? 'pick' : ''].filter(Boolean).join(' ');
  return [
    { text: row.phrase, cls: marks },
    { text: `… (${row.count})`, cls: picked ? 'pick' : 'dim' },
  ];
}

function optionSegs(option: CatalogOption, input: string, picked: boolean): Seg[] {
  const marks = [option.locked ? 'locked' : '', kindClass(option), needsClass(option), picked ? 'pick' : '']
    .filter(Boolean)
    .join(' ');

  const label = highlight(option.label, input).map((seg) => ({
    ...seg,
    cls: [seg.cls, marks].filter(Boolean).join(' ') || undefined,
  }));
  /*
   * Значки идут перед меткой и в её же цвете: оба служебные, ни один не
   * вводится и не попадает в эхо. Заработанность — свойство самой возможности,
   * невозврат — её последствия, поэтому `✦` стоит первым.
   */
  const glyphs = [option.attrs.earned ? EARNED_MARK : '', option.attrs.advance ? ADVANCE_MARK : '']
    .filter(Boolean)
    .join(' ');
  return glyphs === '' ? label : [{ text: `${glyphs} `, cls: marks }, ...label];
}

/**
 * Список команд (07-оболочка-тз, «Список всегда показывает, что можно»).
 *
 * Вертикальный, по одной команде на строку, без горизонтальной раскладки:
 * взгляд движется в одном направлении, `↑`/`↓` всегда значат «предыдущая» и
 * «следующая», а у выбранной строки однозначное место.
 *
 * Категория не владеет ни строкой-заголовком, ни пустым разделителем — список
 * плотный: окружение, сюжет, сюжет с `advance`. Различает их цвет, порядок
 * и знак `▶`, а не геометрические секции.
 *
 * Высота постоянна. Если вариантов больше, окно едет за выбором, а положение
 * ввода, деталей и служебной полосы не меняется.
 */
export function commandLines(
  matches: Row[],
  pick: number | null,
  input: string,
  max: number,
  rows: number,
): Seg[][] {
  // Окно списка держит выбранное на виду; без выбора показываем начало.
  const from = pick == null ? 0 : Math.max(0, Math.min(pick - rows + 1, matches.length - rows));
  const window = matches.slice(Math.max(0, from), Math.max(0, from) + rows);

  const lines = window.map((row, i) => {
    const index = Math.max(0, from) + i;
    const picked = index === pick;
    // Маркер висит в поле, как промпт: сами команды тогда стоят на той же
    // колонке, что набранное и повествование, и левый край остаётся линией.
    const lead: Seg = {
      text: hanging(picked ? PICK_MARK : ' '),
      ...(picked ? { cls: 'pick' } : {}),
    };
    const body = row.kind === 'family' ? familySegs(row, picked) : optionSegs(row.option, input, picked);
    return clip([lead, ...body], max + MARGIN.text);
  });

  const hidden = matches.length - Math.max(0, from) - window.length;
  if (hidden > 0 && lines.length > 0) {
    lines[lines.length - 1] = clip(
      [...lines[lines.length - 1]!, { text: ` +${hidden}`, cls: 'dim' }],
      max + MARGIN.text,
    );
  }

  while (lines.length < rows) lines.push([]);
  return lines.slice(0, rows);
}

/**
 * Область деталей выбранной команды: точная реплика Марго, а под ней — предупре-
 * ждение `advance`. Высота постоянная, поэтому появление предпросмотра не двигает
 * ни список, ни полосу.
 *
 * Предпросмотр приглушён и в поток не попадает: после `Enter` та же реплика
 * придёт как часть сцены, обычным цветом Марго.
 *
 * Длинную реплику разрешено обрезать, пересказывать — нет: дословность головы
 * фразы и есть гарантия, ради которой предпросмотр существует, а многоточие
 * честно говорит, что дальше есть ещё.
 */
export function detailLines(
  preview: string | null,
  advance: boolean,
  needs: Needs | null,
  timer: { left: number; total: number; glyphs: { full: string; empty: string; width: number } } | null,
  max: number,
  rows: number,
): Seg[][] {
  const lines: Seg[][] = [];
  /*
   * Пометы машины — в одну строку под репликой: `advance` и требование говорят
   * о разном, но оба короткие и оба служебные, а высота области постоянна.
   * Требование печатается словами в том же цвете, которым команда покрашена
   * в списке: цвет сказал «нужна вещь», строка называет, какая именно.
   */
  /*
   * Шкала времени забирает вторую строку целиком: в раунде «надавить» нет ни
   * `advance`, ни условных ответов, спорить с ней нечему. Первая строка
   * остаётся предпросмотром — он там нужнее всего, потому что выбирать
   * приходится быстро.
   */
  if (timer) {
    const lines: Seg[][] = [];
    if (preview) {
      // Строка одна: под шкалу уходит вторая. Многоточие ставим только там,
      // где реплика действительно не влезла.
      const all = wrap(`Марго: ${preview}`, max);
      const head = all.length > 1 ? ellipsis(all[0]!, max) : all[0]!;
      lines.push([{ text: pad() }, { text: head, cls: 'preview' }]);
    }
    lines.push([{ text: pad() }, ...timerSegs(timer.left, timer.total, timer.glyphs)]);
    while (lines.length < rows) lines.push([]);
    return lines.slice(0, rows);
  }

  const notes: Seg[] = [];
  if (advance) notes.push({ text: needs ? ADVANCE_SHORT : ADVANCE_LEGEND, cls: 'dim' });
  if (needs) {
    if (notes.length > 0) notes.push({ text: ' · ', cls: 'dim' });
    notes.push({
      text: `${USES[needs.kind]} ${needs.label}`,
      cls: needs.kind === 'word' ? 'needs-word' : 'needs-item',
    });
  }

  if (preview) {
    // Строку под пометы держим только тогда, когда они будут.
    const room = Math.max(1, rows - (notes.length > 0 ? 1 : 0));
    const all = wrap(`Марго: ${preview}`, max);
    const shown = all.slice(0, room);
    if (all.length > room) shown[shown.length - 1] = ellipsis(shown[shown.length - 1]!, max);

    for (const line of shown) lines.push([{ text: pad() }, { text: line, cls: 'preview' }]);
  }
  // Строка помет по ширине колонки не режется: подпись `advance` длиннее
  // колонки намеренно — она объясняет знак, а экран шире текстового блока.
  if (notes.length > 0) lines.push([{ text: pad() }, ...notes]);

  while (lines.length < rows) lines.push([]);
  return lines.slice(0, rows);
}

/**
 * Полоса служебных действий: закреплена внизу и не зависит от ввода. Она часть
 * оболочки, а не содержимого комнаты, — поэтому не исчезает при переходе и не
 * фильтруется набором.
 *
 * `3 управление` стоит здесь ради обнаруживаемости, но командой не является:
 * метаинструкция не притворяется действием терминала.
 */
export function systemLine(commands: string[], max: number): Seg[] {
  const segs: Seg[] = [{ text: pad() }];
  for (const command of [...commands, 'управление']) {
    if (segs.length > 1) segs.push({ text: ' · ', cls: 'dim' });
    const key = command === 'управление' ? MANUAL_HINT : HOTKEY[command];
    if (key) segs.push({ text: `${key} `, cls: 'dim' });
    segs.push({ text: command, cls: 'system' });
  }
  return clip(segs, max + MARGIN.text);
}

export function inputLine(input: string): Seg[] {
  return [
    { text: hanging(), cls: 'dim' },
    { text: input },
    { text: ' ', cls: 'cursor' },
  ];
}

const TITLES: Record<OverlayCommand, string> = {
  справочник: 'СПРАВОЧНИК',
  дело: 'ДЕЛО',
  инвентарь: 'ИНВЕНТАРЬ',
};

export function overlayTitle(call: OverlayCall): string {
  return TITLES[call.kind];
}

/**
 * Контекстная панель по `1`, `2`, `3` (07-оболочка-тз, «Контекстная панель»).
 *
 * Хоткей не исполняет одноимённую команду и не открывает хранилище: он показывает
 * **одну** сущность — ту, о которой игрок сейчас читает. Полный список нужен
 * редко и по делу, а «что это было только что» — постоянно, и ради этого
 * вываливать на человека двадцать аббревиатур значит отвечать не на тот вопрос.
 *
 * Панель занимает нижнюю область целиком и ровно те же строки, что список команд:
 * сетка от неё не должна шевелиться.
 */
const PANEL: Record<EntityKind, { title: string; empty: string; command: string }> = {
  reference: { title: 'справочник', empty: 'термины справочника', command: 'справочник' },
  word: { title: 'дело', empty: 'слова дела', command: 'дело' },
};

const CLOSE_HINT = 'Esc закрыть';
const SWITCH_HINT = '← → другие';

/** Карточка слова растёт от источников, но не раскрывает их заранее. */
function wordText(word: GameContent['words'][string] | undefined, save: SaveState): string {
  if (!word) return '';
  return [word.text, ...(word.details ?? []).filter((part) => evalCondition(part.if, save)).map((part) => part.text)]
    .filter((part) => part.trim() !== '')
    .join('\n\n');
}

/** Что показывает панель: статья, карточка слова с происхождением, вступление предмета. */
function entityCard(kind: EntityKind, id: string, content: GameContent, save: SaveState): Seg[][] {
  const out: Seg[][] = [];
  if (kind === 'reference') {
    const term = content.reference[id];
    if (!term) return [[]];
    out.push([{ text: term.label }, { text: '  ' }, { text: term.category, cls: 'dim' }]);
    out.push([]);
    out.push([{ text: plainText(term.text), cls: 'dim' }]);
    return out;
  }
  if (kind === 'word') {
    const word = content.words[id];
    const state = save.words[id];
    out.push([
      { text: word?.label ?? id },
      { text: '  ' },
      { text: word?.category ?? '', cls: 'dim' },
      { text: '  ' },
      { text: state === 'white' ? '[белое]' : '[серое]', cls: state === 'white' ? 'dim' : 'locked' },
    ]);
    out.push([]);
    out.push([{ text: plainText(wordText(word, save)), cls: 'dim' }]);
    return out;
  }
  return out;
}

/**
 * Компактный инвентарь по `3` (07-оболочка-тз, «Компактный инвентарь по `3`»).
 *
 * Предметы не знание: они не размечаются в тексте, не попадают в сессионную
 * историю и поток не прокручивают. Поэтому и панель у них своя — не «где я это
 * встречал», а «что у меня сейчас с собой».
 *
 * Показывает одну вещь: карточку и то, что вещь умеет. Умения показаны, но
 * отсюда не исполняются — сделать ими что-то можно на полном экране `инвентарь`
 * или командой, которую объявила сцена. Панель отвечает на вопрос «что у меня
 * есть», а не заменяет собой список действий.
 */
export function inventoryLines(
  content: GameContent,
  save: SaveState,
  index: number,
  cols: number,
  rows: number,
): Seg[][] {
  const max = Math.max(1, cols - MARGIN.text - MARGIN.right);
  const body: Seg[][] = [];
  const items = save.inventory;

  if (items.length === 0) {
    for (const line of wrap('На руках сейчас ничего нет.', max)) body.push([{ text: line, cls: 'dim' }]);
    body.push([]);
    body.push(
      spread([{ text: 'Полный список: «инвентарь».', cls: 'dim' }], [{ text: CLOSE_HINT, cls: 'dim' }], max),
    );
    return frame(body, [], rows);
  }

  const at = Math.min(Math.max(index, 0), items.length - 1);
  const id = items[at]!;
  const doc = Object.values(content.docs).find((d) => d.id === id);

  body.push(
    spread(
      [{ text: doc?.label ?? id }],
      items.length > 1 ? [{ text: `${at + 1}/${items.length}`, cls: 'dim' }] : [],
      max,
    ),
  );
  body.push([]);
  for (const line of wrap(plainText(doc?.nodes[0]?.text ?? ''), max)) body.push([{ text: line, cls: 'dim' }]);

  const actions = doc ? itemActions(content, save, doc.docId) : [];
  if (actions.length > 0) {
    body.push([]);
    for (const action of actions) body.push([{ text: action.label, cls: 'environment' }]);
  }

  const hints = spread(
    items.length > 1 ? [{ text: SWITCH_HINT, cls: 'dim' }] : [],
    [{ text: CLOSE_HINT, cls: 'dim' }],
    max,
  );
  return frame(body, hints, rows);
}

/**
 * Панель занимает ровно отведённые строки: подсказка прижата к низу, между ней
 * и текстом — воздух. Сетка от открытия панели шевелиться не должна.
 */
function frame(body: Seg[][], hints: Seg[], rows: number): Seg[][] {
  const shown = body.slice(0, Math.max(0, rows - 2));
  const filler = Math.max(0, rows - 1 - shown.length);
  return [
    ...shown.map((line) => [{ text: pad() }, ...line]),
    ...Array.from({ length: filler }, () => [] as Seg[]),
    [{ text: pad() }, ...hints],
  ];
}

export function contextLines(
  kind: EntityKind,
  entities: SessionEntity[],
  index: number,
  content: GameContent,
  save: SaveState,
  cols: number,
  rows: number,
): Seg[][] {
  const max = Math.max(1, cols - MARGIN.text - MARGIN.right);
  const body: Seg[][] = [];
  const names = PANEL[kind];

  if (entities.length === 0) {
    // Пустая история — не пустая панель: игроку говорят, где искать полный список.
    for (const line of wrap(`В этой сессии ещё не встречались ${names.empty}.`, max)) {
      body.push([{ text: line, cls: 'dim' }]);
    }
    body.push([]);
    body.push(spread([{ text: `Полный список: «${names.command}».`, cls: 'dim' }], [{ text: CLOSE_HINT, cls: 'dim' }], max));
  } else {
    const current = entities[Math.min(Math.max(index, 0), entities.length - 1)]!;
    body.push(
      spread(
        [{ text: names.title, cls: 'dim' }],
        entities.length > 1 ? [{ text: `${index + 1}/${entities.length}`, cls: 'dim' }] : [],
        max,
      ),
    );
    body.push([]);
    // Карточка приходит сегментами, но длинный текст всё равно надо переносить.
    for (const line of entityCard(kind, current.id, content, save)) {
      const plain = line.map((seg) => seg.text).join('');
      if (line.length === 1 && plain.length > max) {
        for (const wrapped of wrap(plain, max)) body.push([{ text: wrapped, cls: line[0]!.cls }]);
      } else {
        body.push(line);
      }
    }
  }

  const hints = spread(
    entities.length > 1 ? [{ text: SWITCH_HINT, cls: 'dim' }] : [],
    entities.length === 0 ? [] : [{ text: CLOSE_HINT, cls: 'dim' }],
    max,
  );
  return frame(body, hints, rows);
}

/**
 * Что сейчас выбрано в хранилище вещей: сама вещь и, если её открыли, её действие.
 * Живёт в оболочке, а не в сессии: это положение курсора, а не состояние мира.
 */
export interface StoragePick {
  pick: number;
  open: string | null;
  act: number;
}

/**
 * Экран `предметы` ([[99-открытые-вопросы]], «Глаголы и состояния предметов»).
 *
 * Сначала вещи, потом — действия выбранной. Собственные действия предмета живут
 * здесь, а не в списке команд: иначе с ростом инвентаря они вытеснят из него то,
 * ради чего сцена написана. Предмет достают, когда о нём вспомнили, и это
 * отдельный жест — ровно как в жизни лезут в карман.
 */
function storageLines(
  content: GameContent,
  save: SaveState,
  max: number,
  storage: StoragePick | null,
): Seg[][] {
  const out: Seg[][] = [];
  const wrapped = (text: string, cls?: string) => {
    for (const line of wrap(text, max - MARGIN.text)) out.push([{ text: pad() }, { text: line, cls }]);
  };

  if (save.inventory.length === 0) return [[{ text: 'На руках ничего нет.', cls: 'dim' }]];

  const items = save.inventory.map((id) => ({ id, doc: Object.values(content.docs).find((d) => d.id === id) }));
  const at = Math.min(Math.max(storage?.pick ?? 0, 0), items.length - 1);
  const opened = storage?.open ?? null;

  if (opened == null) {
    items.forEach((item, i) => {
      const picked = i === at;
      out.push([
        { text: picked ? hanging(PICK_MARK) : pad(), cls: picked ? 'accent' : undefined },
        { text: item.doc?.label ?? item.id, cls: picked ? undefined : 'dim' },
      ]);
    });
    out.push([]);
    // Описание — только у выбранной вещи: список должен читаться списком.
    wrapped(plainText(items[at]?.doc?.nodes[0]?.text ?? ''), 'dim');
    return out;
  }

  const doc = content.docs[opened];
  out.push([{ text: pad() }, { text: doc?.label ?? opened }]);
  out.push([]);
  wrapped(plainText(doc?.nodes[0]?.text ?? ''), 'dim');
  out.push([]);

  const actions = itemActions(content, save, opened);
  if (actions.length === 0) {
    wrapped('С ней сейчас ничего не сделать.', 'dim');
    return out;
  }
  const act = Math.min(Math.max(storage?.act ?? 0, 0), actions.length - 1);
  actions.forEach((action, i) => {
    const picked = i === act;
    out.push([
      { text: picked ? hanging(PICK_MARK) : pad(), cls: picked ? 'accent' : undefined },
      { text: action.label, cls: picked ? 'environment' : 'dim' },
    ]);
  });
  return out;
}

export function overlayLines(
  call: OverlayCall,
  content: GameContent,
  save: SaveState,
  max: number,
  storage: StoragePick | null = null,
): Seg[][] {
  const { kind } = call;
  const out: Seg[][] = [];
  const push = (text: string, cls?: string) => {
    for (const line of wrap(text, max)) out.push([{ text: line, cls }]);
  };

  if (kind === 'дело') {
    const words = Object.entries(save.words);
    if (words.length === 0) return [[{ text: 'Дело пустое.', cls: 'dim' }]];
    for (const [id, state] of words) {
      const word = content.words[id];
      out.push(
        spread(
          [{ text: word?.label ?? id }],
          [{ text: state === 'white' ? '[белое]' : '[серое]', cls: state === 'white' ? 'dim' : 'locked' }],
          max,
        ),
      );
      push(plainText(wordText(word, save)), 'dim');
      out.push([]);
    }
    return out;
  }

  if (kind === 'инвентарь') {
    return storageLines(content, save, max, storage);
  }

  /*
   * Справочник — список статей, отсортированный по категориям: двадцать
   * аббревиатур подряд читаются хуже, чем те же двадцать, разложенные
   * на «физика», «места», «порядок». Сортировка — единственное, ради чего
   * категория существует.
   */
  const terms = Object.values(content.reference)
    .sort((a, b) => a.category.localeCompare(b.category) || a.label.localeCompare(b.label));
  if (terms.length === 0) return [[{ text: 'Справочник пуст.', cls: 'dim' }]];

  const width = Math.max(...terms.map((term) => term.label.length));
  let category: string | null = null;
  for (const term of terms) {
    if (term.category !== category) {
      if (category != null) out.push([]);
      category = term.category;
      out.push([{ text: category, cls: 'dim' }]);
    }
    // Статья длиннее строки переносится с отступом под название: список должен
    // читаться колонкой, а не сползать в абзац.
    const lines = wrap(plainText(term.text), Math.max(1, max - width - 2));
    lines.forEach((line, i) => {
      out.push([
        { text: (i === 0 ? term.label : '').padEnd(width) },
        { text: '  ' },
        { text: line, cls: 'dim' },
      ]);
    });
  }
  return out;
}
